import React, { useState, useRef, useCallback } from 'react';
import { ItemsApiProvider } from '../../providers/ItemsApiProvider';
import { IPendingUpload } from '../container-action-bar/types/IPendingUpload';
import { handleUploadErrors, UploadError } from '../../utils/uploadErrorHandler';
import './DragDropArea.css';
import { useBlacklistedExtensionsContext } from '../../common/BlacklistedExtensions';
import { Caption1 } from '@fluentui/react-components';

interface DragDropAreaProps {
    children: React.ReactNode;
    containerId: string;
    parentId: string;
    parentName: string;
    onItemsUpdated?: () => void;
    onUploadProgress?: (uploads: Map<string, IPendingUpload>) => void;
    onError?: (message: string, details?: string) => void;
    disabled?: boolean;
}

const itemsApi = ItemsApiProvider.instance;

export const DragDropArea: React.FC<DragDropAreaProps> = ({
    children,
    containerId: containerId,
    parentId,
    parentName,
    onItemsUpdated,
    onUploadProgress,
    onError,
    disabled = false
}) => {
    const [isDragActive, setIsDragActive] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);

    const blacklistedExtensions = useBlacklistedExtensionsContext();
    const blacklistedEtensionsText = blacklistedExtensions.length ? `Disallowed extensions: ${blacklistedExtensions.join(", ")} .` : '';

    const dragCounter = useRef(0);
    const uploads = useRef(new Map<string, IPendingUpload>());

    const showUploadError = useCallback((message: string, details?: string) => {
        onError?.(message, details);
    }, [onError]);

    const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (disabled) return;

        dragCounter.current++;

        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragActive(true);
        }
    }, [disabled]);

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (disabled) return;

        dragCounter.current--;

        if (dragCounter.current === 0) {
            setIsDragActive(false);
            setIsDragOver(false);
        }
    }, [disabled]);

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (disabled) return;

        setIsDragOver(true);

        // Set the drop effect to copy
        if (e.dataTransfer) {
            e.dataTransfer.dropEffect = 'copy';
        }
    }, [disabled]);

    const processFileEntry = useCallback(async (entry: FileSystemFileEntry): Promise<File> => {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('File entry processing timeout'));
            }, 5000);

            entry.file(
                (file) => {
                    clearTimeout(timeout);
                    resolve(file);
                },
                (error) => {
                    clearTimeout(timeout);
                    reject(error);
                }
            );
        });
    }, []);

    const processDirectoryEntries = async (entries: FileSystemEntry[], path: string, files: File[]): Promise<void> => {
        const processPromises = entries.map(async (entry) => {
            const currentPath = path ? `${path}/${entry.name}` : entry.name;

            if (entry.isFile) {
                try {
                    const file = await processFileEntry(entry as FileSystemFileEntry);
                    // Create a new file with the full path as name for proper folder structure
                    const fileWithPath = new File([file], currentPath, { type: file.type });
                    files.push(fileWithPath);
                } catch (error) {
                    console.warn(`Failed to process file entry: ${entry.name}`, error);
                }
            } else if (entry.isDirectory) {
                try {
                    const nestedFiles = await processDirectoryEntry(entry as FileSystemDirectoryEntry, currentPath);
                    files.push(...nestedFiles);
                } catch (error) {
                    console.warn(`Failed to process directory entry: ${entry.name}`, error);
                }
            }
        });

        await Promise.allSettled(processPromises);
    };

    const readDirectoryEntries = useCallback((reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> => {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('Directory reading timeout'));
            }, 5000);

            reader.readEntries(
                (entries) => {
                    clearTimeout(timeout);
                    resolve(entries);
                },
                (error) => {
                    clearTimeout(timeout);
                    reject(error);
                }
            );
        });
    }, []);

    const processDirectoryEntry = useCallback(async (entry: FileSystemDirectoryEntry, path: string = ''): Promise<File[]> => {
        try {
            const reader = entry.createReader();
            let allEntries: FileSystemEntry[] = [];
            let attempts = 0;
            const maxAttempts = 10; // Prevent infinite loops

            // Keep reading until we get all entries (directories might have many files)
            while (attempts < maxAttempts) {
                try {
                    const entries = await readDirectoryEntries(reader);
                    if (entries.length === 0) break;
                    allEntries = allEntries.concat(entries);
                    attempts++;
                } catch (error) {
                    console.warn(`Failed to read directory entries on attempt ${attempts + 1}:`, error);
                    break;
                }
            }

            const files: File[] = [];
            await processDirectoryEntries(allEntries, path, files);
            return files;
        } catch (error) {
            console.warn(`Failed to process directory entry: ${entry.name}`, error);
            // Return empty array to indicate no files were processed due to error
            return [];
        }
    }, [readDirectoryEntries, processFileEntry]);

    const uploadFiles = useCallback(async (files: File[]) => {
        const uploadErrors: UploadError[] = [];
        let successfulUploads = 0;

        try {
            const newUploads = new Map(uploads.current);
            const uploadPromises: Promise<void>[] = [];

            for (const file of files) {
                const fileName = file.name;

                // Create the actual file object for upload (without folder path in name)
                const actualFileName = fileName.includes('/') ? fileName.split('/').pop()! : fileName;
                const fileForUpload = new File([file], actualFileName, { type: file.type });

                const upload: IPendingUpload = {
                    containerId: containerId,
                    parentId: parentId,
                    file: fileForUpload,
                    uploadTask: itemsApi.createFile(containerId, parentId, fileForUpload, blacklistedExtensions)
                };

                // Use the original fileName (with path) for unique identification
                const uploadId = `${upload.containerId}/${upload.parentId}/${fileName}`;
                newUploads.set(uploadId, upload);

                // Create a promise for this upload
                const uploadPromise = upload.uploadTask
                    .then(() => {
                        newUploads.delete(uploadId);
                        uploads.current = new Map(newUploads);
                        onUploadProgress?.(uploads.current);
                        successfulUploads++;
                    })
                    .catch((err) => {
                        newUploads.delete(uploadId);
                        uploads.current = new Map(newUploads);
                        onUploadProgress?.(uploads.current);
                        uploadErrors.push({
                            fileName: fileName,
                            error: err instanceof Error ? err.message : 'Please check your permissions or try again later'
                        });
                    });

                uploadPromises.push(uploadPromise);
            }

            uploads.current = newUploads;
            onUploadProgress?.(uploads.current);

            // Wait for all uploads to complete
            await Promise.allSettled(uploadPromises);

            // Refresh the list if any uploads succeeded, regardless of errors
            if (successfulUploads > 0) {
                onItemsUpdated?.();
            }

            // Show errors only if there were any
            handleUploadErrors(uploadErrors, showUploadError);

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to upload file(s)";
            const errorDetails = err instanceof Error ? '' : 'Please check your permissions or try again later';
            showUploadError(errorMessage, errorDetails);
        }
    }, [containerId, parentId, parentName, onItemsUpdated, onUploadProgress, showUploadError]);

    const processFileItem = useCallback(async (item: DataTransferItem): Promise<File[]> => {
        try {
            const entry = item.webkitGetAsEntry();

            if (entry) {
                if (entry.isFile) {
                    const file = await processFileEntry(entry as FileSystemFileEntry);
                    return [file];
                } else if (entry.isDirectory) {
                    // For directories, use the entry name directly - this will preserve the real folder name
                    const folderFiles = await processDirectoryEntry(entry as FileSystemDirectoryEntry, entry.name);
                    return folderFiles;
                }
            } else {
                // Fallback for browsers that don't support webkitGetAsEntry
                const file = item.getAsFile();
                if (file) {
                    return [file];
                }
            }

            return [];
        } catch (error) {
            console.warn('Failed to process file item:', error);
            return [];
        }
    }, [processFileEntry, processDirectoryEntry]);

    const processDroppedItems = useCallback(async (items: DataTransferItemList): Promise<File[]> => {
        const files: File[] = [];
        const itemsArray = Array.from(items);

        // Process items concurrently but with error handling
        const promises = itemsArray.map(async (item) => {
            if (item.kind === 'file') {
                try {
                    const itemFiles = await processFileItem(item);
                    return itemFiles;
                } catch (error) {
                    console.warn('Failed to process item:', error);
                    return [];
                }
            }
            return [];
        });

        const results = await Promise.allSettled(promises);

        // Collect all successfully processed files
        results.forEach((result) => {
            if (result.status === 'fulfilled') {
                files.push(...result.value);
            }
        });

        return files;
    }, [processFileItem]);

    const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (disabled) return;

        setIsDragActive(false);
        setIsDragOver(false);
        dragCounter.current = 0;

        let files: File[] = [];

        if (e.dataTransfer.items) {
            // Handle DataTransferItemList (supports folders)
            files = await processDroppedItems(e.dataTransfer.items);
        } else if (e.dataTransfer.files) {
            // Fallback to FileList (files only, no folder support)
            files = Array.from(e.dataTransfer.files);
        }

        if (files.length > 0) {
            await uploadFiles(files);
        }
    }, [disabled, processDroppedItems, uploadFiles]);

    const dragDropClasses = [
        'drag-drop-area',
        isDragActive ? 'drag-active' : '',
        isDragOver ? 'drag-over' : '',
        disabled ? 'disabled' : ''
    ].filter(Boolean).join(' ');

    return (
        <div
            className={dragDropClasses}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {children}
            {isDragActive && !disabled && (
                <div className="drag-overlay">
                    <div className="drag-message">
                        <div className="drag-icon">📁</div>
                        <div className="drag-text">
                            {isDragOver ? 'Drop files and folders here' : 'Drop to upload'}
                        </div>
                        {
                            blacklistedEtensionsText && (
                                <Caption1 className="blacklisted-extensions">{blacklistedEtensionsText}</Caption1>
                            )
                        }
                    </div>
                </div>
            )}
        </div>
    );
};
